import { Helmet } from "react-helmet-async";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollFadeWrapper } from "@/components/ui/scroll-fade-wrapper";
import { ArrowRight, HelpCircle } from "lucide-react";
import peptideRoutineImg from "@/assets/peptide-routine.jpg";
import { clinicalData } from "@/data/peptide-clinical-data";
import { APP_URL } from "@/lib/app-config";

const PeptideEducation = () => {
  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Peptide Therapy Guide | Elara Health Education</title>
        <meta name="description" content="Explore our comprehensive guide to peptide therapies — from weight management to recovery, sleep, and anti-aging. Science-backed, physician-guided." />
        <link rel="canonical" href={`${APP_URL}/peptides`} />
        <meta property="og:title" content="Peptide Therapy Guide | Elara Health Education" />
        <meta property="og:description" content="Explore our comprehensive guide to peptide therapies — from weight management to recovery, sleep, and anti-aging." />
        <meta property="og:url" content={`${APP_URL}/peptides`} />
        <meta property="og:image" content={`${APP_URL}/og-image.jpg`} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "MedicalWebPage",
              name: "Peptide Therapy Guide",
              description: "Comprehensive guide to peptide therapies — weight management, recovery, sleep, anti-aging, and more. Science-backed, physician-guided.",
              url: `${APP_URL}/peptides`,
              specialty: "Endocrinology",
              about: clinicalData.map(cat => ({
                "@type": "MedicalTherapy",
                name: cat.patientTitle,
                description: cat.description,
              })),
              publisher: { "@id": `${APP_URL}/#organization` },
            },
            {
              "@type": "FAQPage",
              mainEntity: [
                {
                  "@type": "Question",
                  name: "What is peptide therapy?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "Peptide therapy uses natural molecules made of amino acids — the same building blocks your body already uses — to send precise signals that help your cells heal, grow, protect, and optimize. Treatments are prescribed by licensed clinicians and tailored to individual health goals.",
                  },
                },
                {
                  "@type": "Question",
                  name: "Is peptide therapy safe?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "Peptide therapies are generally well-tolerated with minimal side effects. The most common reaction is mild irritation at the injection site. Every Elara Health protocol begins with a comprehensive health assessment, lab testing, and a personalized treatment plan designed by a licensed clinician.",
                  },
                },
                {
                  "@type": "Question",
                  name: "How are peptides administered?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "Peptides are delivered through several routes depending on the therapy: subcutaneous injection (most common), oral or sublingual tablets, nasal sprays (for brain-focused peptides), and topical creams (for skin therapies).",
                  },
                },
                {
                  "@type": "Question",
                  name: "What conditions can peptide therapy help with?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "Peptide therapy can support weight management and metabolic optimization, muscle recovery and performance, cognitive function and neuroprotection, immune modulation and tissue repair, anti-aging and longevity, and sexual health. Treatment is personalized based on your health assessment.",
                  },
                },
                {
                  "@type": "Question",
                  name: "How do I get started with peptide therapy at Elara Health?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "Take a 5-minute health assessment online. A licensed clinician will review your results, design a personalized treatment plan, and prescription medications are delivered directly to your door from U.S.-licensed compounding pharmacies.",
                  },
                },
              ],
            },
          ],
        })}</script>
      </Helmet>
      <Navbar />
      <div className="pt-24 pb-16">
        {/* Hero with image */}
        <section className="container mx-auto px-4 mb-16">
          <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-4">
              <p className="text-sm font-semibold uppercase tracking-widest text-champagne">
                Peptide Therapy Guide
              </p>
              <h1 className="font-display text-4xl md:text-5xl text-foreground">
                Understanding Peptide Therapy
              </h1>
              <p className="text-lg text-muted-foreground">
                Peptides are natural molecules made of amino acids — the same building blocks your body already uses. They act as precise messengers, telling your cells to heal, grow, protect, and optimize.
              </p>
            </div>
            <div className="relative">
              <img
                src={peptideRoutineImg}
                alt="Patient preparing a peptide therapy routine in a calming home setting"
                loading="lazy"
                width={1024}
                height={1024}
                className="rounded-2xl shadow-lg w-full h-auto object-cover aspect-[4/5]"
              />
            </div>
          </div>
        </section>

        {/* What Are Peptides */}
        <section className="container mx-auto px-4 mb-16">
          <div className="max-w-4xl mx-auto grid md:grid-cols-5 gap-4">
            {[
              { label: "Hormonal Balance", desc: "Supports your body's natural hormone production" },
              { label: "Fat Metabolism", desc: "Targets stubborn fat and regulates appetite" },
              { label: "Tissue Repair", desc: "Accelerates healing and reduces inflammation" },
              { label: "Immune Support", desc: "Strengthens your body's defense systems" },
              { label: "Brain Health", desc: "Enhances focus, memory, and mental clarity" },
            ].map((item) => (
              <Card key={item.label} className="border-border bg-card text-center">
                <CardContent className="p-5">
                  <p className="font-display text-sm text-foreground mb-1">{item.label}</p>
                  <p className="text-xs text-muted-foreground">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Categories */}
        <section className="container mx-auto px-4 mb-16">
          <Tabs defaultValue="weight" className="max-w-5xl mx-auto">
            <ScrollFadeWrapper fadeColor="from-secondary">
            <TabsList className="flex overflow-x-auto no-scrollbar h-auto gap-1 bg-secondary p-1.5 w-full justify-start">
              {clinicalData.map((cat) => (
                <TabsTrigger key={cat.id} value={cat.id} className="gap-1.5 text-xs shrink-0">
                  <cat.icon size={14} /> {cat.patientTitle}
                </TabsTrigger>
              ))}
            </TabsList>
            </ScrollFadeWrapper>

            {clinicalData.map((cat) => (
              <TabsContent key={cat.id} value={cat.id} className="mt-6">
                <div className="mb-6">
                  <h2 className="font-display text-3xl text-foreground mb-2">{cat.patientTitle}</h2>
                  <p className="text-muted-foreground">{cat.description}</p>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  {cat.peptides.map((p) => (
                    <Card key={p.name} className="border-border bg-card hover:shadow-md transition-shadow">
                      <CardContent className="p-5">
                        <h3 className="font-display text-lg text-foreground mb-2">{p.name}</h3>
                        <p className="text-sm text-muted-foreground leading-relaxed">{p.simple}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </section>

        {/* Safety */}
        <section className="container mx-auto px-4 mb-16">
          <Card className="max-w-4xl mx-auto border-border bg-secondary/30">
            <CardContent className="p-8">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-1">
                  <HelpCircle size={20} className="text-primary" />
                </div>
                <div className="space-y-3">
                  <h3 className="font-display text-xl text-foreground">Is Peptide Therapy Safe?</h3>
                  <div className="space-y-2 text-sm text-muted-foreground leading-relaxed">
                    <p>
                      Peptide therapies are generally <strong className="text-foreground">well-tolerated</strong> with minimal side effects. The most common reaction is mild irritation at the injection site.
                    </p>
                    <p>
                      Peptides are delivered through several routes depending on the therapy: <strong className="text-foreground">subcutaneous injection</strong> (most common), <strong className="text-foreground">oral or sublingual</strong> tablets, <strong className="text-foreground">nasal sprays</strong> (for brain-focused peptides), and <strong className="text-foreground">topical creams</strong> (for skin therapies).
                    </p>
                    <p>
                      Every Elara Health protocol begins with a <strong className="text-foreground">comprehensive health assessment</strong>, <strong className="text-foreground">lab testing</strong>, and a <strong className="text-foreground">personalized treatment plan</strong> designed by a licensed clinician.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* CTA */}
        <section className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center space-y-4">
            <h2 className="font-display text-3xl text-foreground">Ready to get started?</h2>
            <p className="text-muted-foreground">
              Take a 5-minute health assessment and a licensed clinician will design a personalized peptide protocol just for you.
            </p>
            <Link to="/get-started">
              <Button size="lg" className="gap-2">
                Start Your Assessment <ArrowRight size={18} />
              </Button>
            </Link>
          </div>
        </section>
      </div>
      <Footer />
    </div>
  );
};

export default PeptideEducation;
